import { FilesClass } from './../models/uploads-class';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, of } from 'rxjs';
import { catchError, tap, map, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  ASFMatricula,
  InsurancePolicyClass,
  InsurancePolicyCompanyRequestsQuizClass,
  InsurancePolicyReceipts,
  InsurancePolicyRisks,
  InsurancePolicyStatusTypesClass,
} from '../models/insurancesPolicy-class';
import { Alert } from '../models/alert-class';
import { HttpclienterrorService } from '../interceptor/httpclienterror.service';
import { GoogleAnalyticsService } from './google-analytics.service';
import { FacebookPixelService } from './facebook-pixel.service';
import { CrmCacheService, CrmSwrOptions } from './crm-cache.service';

@Injectable({
  providedIn: 'root',
})
export class InsurancePolicyService {
  constructor(
    private http: HttpClient,
    public httpclienterrorService: HttpclienterrorService,
    private ga: GoogleAnalyticsService,
    private fbPixel: FacebookPixelService,
    private crmCache: CrmCacheService,
  ) {}

  ClientAreaGetInsurancePolicyRisks(
    idInsurancePolicy: number,
    options?: CrmSwrOptions,
  ): Observable<InsurancePolicyRisks[]> {
    return this.crmCache.swr(
      `pol_risks:${idInsurancePolicy}`,
      () =>
        this.http
          .get<
            InsurancePolicyRisks[]
          >(environment.URL + `/insurancepolicy/ClientAreaGetInsurancePolicyRisks/${idInsurancePolicy}`)
          .pipe(
            catchError(error => {
              const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
              return throwError(() => errorMessage);
            }),
          ),
      { persist: false, silentPrefetch: true, ...options },
    );
  }

  ClientAreaGetInsurancePolicyReceipts(
    idCustomer: number,
    idInsurancePolicy: number,
    options?: CrmSwrOptions,
  ): Observable<InsurancePolicyReceipts[]> {
    return this.crmCache.swr(
      `pol_receipts:${idCustomer}:${idInsurancePolicy}`,
      () =>
        this.http
          .get<
            InsurancePolicyReceipts[]
          >(environment.URL + `/insurancepolicy/ClientAreaGetInsurancePolicyReceipts/${idCustomer}/${idInsurancePolicy}`)
          .pipe(
            catchError(error => {
              const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
              return throwError(() => errorMessage);
            }),
          ),
      { persist: false, silentPrefetch: true, ...options },
    );
  }

  ManagementSetInsurancePolicyCompanyRequestsQuiz(
    insurancePolicyCompanyRequestsQuizClass: InsurancePolicyCompanyRequestsQuizClass,
    uuid: string,
  ): Observable<any> {
    // tslint:disable-next-line:object-literal-shorthand
    insurancePolicyCompanyRequestsQuizClass.UUIDLastChange = uuid;

    return this.http
      .post(
        environment.URL + `/insurancepolicy/ManagementSetInsurancePolicyCompanyRequestsQuiz`,
        insurancePolicyCompanyRequestsQuizClass,
      )
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ManagementGetInsurancePolicyCompanyRequestsQuiz(
    idInsurancePolicyCompanyRequestsQuizOwner: number,
    idCompanie: number,
    idProduct: number,
    idProductsTypes: number,
  ): Observable<InsurancePolicyCompanyRequestsQuizClass[]> {
    return this.http
      .get<
        InsurancePolicyCompanyRequestsQuizClass[]
      >(environment.URL + `/insurancepolicy/ManagementGetInsurancePolicyCompanyRequestsQuiz/${idInsurancePolicyCompanyRequestsQuizOwner}/${idCompanie}/${idProduct}/${idProductsTypes}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ManagementGetInsurancePolicyCompanyRequestsQuizID(
    idInsurancePolicyCompanyRequestsQuiz: number,
  ): Observable<InsurancePolicyCompanyRequestsQuizClass[]> {
    return this.http
      .get<
        InsurancePolicyCompanyRequestsQuizClass[]
      >(environment.URL + `/insurancepolicy/ManagementGetInsurancePolicyCompanyRequestsQuizID/${idInsurancePolicyCompanyRequestsQuiz}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetASFMatriculaDados(Matricula: string): Observable<ASFMatricula> {
    const matriculaLimpa = Matricula ? Matricula.trim() : '';
    if (!matriculaLimpa) {
      return of(new ASFMatricula());
    }

    let matricula = new ASFMatricula();
    matricula.Matricula = matriculaLimpa;

    return this.http.post<ASFMatricula>(environment.URLROBOT + `/robotASF/ASFMatricula`, matricula).pipe(
      timeout(5000),
      catchError(error => {
        console.warn('[InsurancePolicyService] Falha ou timeout no robô ASF — fallback gracioso ativo:', error);
        return of(new ASFMatricula());
      }),
    );
  }

  ClientAreaGetInsurancePolicyCompanieProduct(
    idCustomer: number,
    idCompanie: number,
    idProduct: number,
  ): Observable<InsurancePolicyClass[]> {
    return this.http
      .get<
        InsurancePolicyClass[]
      >(environment.URL + `/insurancepolicy/ClientAreaGetInsurancePolicyCompanieProduct/${idCustomer}/${idCompanie}/${idProduct}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaCheckDatainsurance(idCustomer: number): Observable<Alert[]> {
    return this.http.get<Alert[]>(environment.URL + `/insurancepolicy/ClientAreaCheckDatainsurance/${idCustomer}`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetInsurancePolicyAll(options?: CrmSwrOptions): Observable<InsurancePolicyClass[]> {
    return this.crmCache.swr(
      'pol_all_list',
      force => {
        const q = force ? '?refresh=1' : '';
        return this.http
          .get<InsurancePolicyClass[]>(environment.URL + `/insurancepolicy/GetInsurancePolicyAll${q}`)
          .pipe(
            catchError(error => {
              const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
              return throwError(() => errorMessage);
            }),
          );
      },
      options,
    );
  }

  GetCrmDashboardPolicies(options?: CrmSwrOptions): Observable<any> {
    return this.crmCache.swr(
      'pol_crm_dashboard',
      force => {
        const q = force ? '?refresh=1' : '';
        return this.http.get<any>(environment.URL + `/insurancepolicy/GetCrmDashboardPolicies${q}`).pipe(
          catchError(error => {
            const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
            return throwError(() => errorMessage);
          }),
        );
      },
      options,
    );
  }

  GetInsurancePolicyPaginated(
    offset: number,
    limit: number,
    search: string,
    viewMode: string = 'policies',
    idCompanie: number = 0,
    status: string = 'all',
    options?: CrmSwrOptions,
  ): Observable<any> {
    const key = `pol_paginated:${offset}:${limit}:${search || ''}:${viewMode}:${idCompanie}:${status}`;
    return this.crmCache.swr(
      key,
      force => {
        const q = force ? '?refresh=1' : '';
        return this.http
          .post<any>(environment.URL + `/insurancepolicy/GetInsurancePolicyPaginated${q}`, {
            offset,
            limit,
            search,
            viewMode,
            idCompanie,
            status,
          })
          .pipe(
            catchError(error => {
              const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
              return throwError(() => errorMessage);
            }),
          );
      },
      options,
    );
  }

  GetCrmDashboardComercial(ano?: number, idCommercial?: number, options?: CrmSwrOptions): Observable<any> {
    const key = `pol_crm_comercial:${ano || ''}:${idCommercial ?? ''}`;
    return this.crmCache.swr(
      key,
      force => {
        let url = environment.URL + `/insurancepolicy/GetCrmDashboardComercial`;
        const query = [];
        if (ano) query.push(`ano=${ano}`);
        if (idCommercial !== undefined && idCommercial !== null) query.push(`idCommercial=${idCommercial}`);
        if (force) query.push('refresh=1');
        if (query.length > 0) url += `?${query.join('&')}`;

        return this.http.get<any>(url).pipe(
          catchError(error => {
            const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
            return throwError(() => errorMessage);
          }),
        );
      },
      options,
    );
  }

  ClientAreaSetInsurancePolicyFile(file: FilesClass): Observable<any> {
    return this.http.post(environment.URL + `/insurancepolicy/ClientAreaSetInsurancePolicyFile`, file).pipe(
      tap(() => {
        if (file?.idInsurancePolicy) {
          this.crmCache.invalidate(`pol_files:${file.idInsurancePolicy}`);
        }
        this.crmCache.invalidateCurrentScope();
      }),
    );
  }

  ClientAreaSetInsurancePolicy(insurancePolicyClass: InsurancePolicyClass, uuid: string): Observable<any> {
    // tslint:disable-next-line:object-literal-shorthand
    insurancePolicyClass.UUIDLastChange = uuid;

    // Sanitize NIB to avoid validation errors if it's null or the string "null"
    if (insurancePolicyClass.NIB === 'null' || (insurancePolicyClass.NIB as any) === null) {
      insurancePolicyClass.NIB = '';
    }

    return this.http.post(environment.URL + `/insurancepolicy/ClientAreaSetInsurancePolicy`, insurancePolicyClass).pipe(
      tap((result: any) => {
        const polId = result?.idInsurancePolicy || insurancePolicyClass?.idInsurancePolicy;
        if (polId) {
          this.crmCache.invalidate(`pol_files:${polId}`);
        }
        this.crmCache.invalidateCurrentScope();
        // Track GA4 Purchase
        this.ga.trackPurchase(
          result?.idInsurancePolicy?.toString() || 'tx_' + Date.now(),
          insurancePolicyClass.Price || 0,
          insurancePolicyClass.PolicyNumber || 'Apólice',
          'pol_' + insurancePolicyClass.idInsurancePolicy,
        );

        // Track Meta Pixel Purchase
        this.fbPixel.trackPurchase(
          insurancePolicyClass.Price || 0,
          'pol_' + insurancePolicyClass.idInsurancePolicy,
          insurancePolicyClass.PolicyNumber || 'Apólice',
        );
      }),
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  ClientAreaGetInsurancePolicyId(
    idInsurancePolicy: number,
    options?: CrmSwrOptions,
  ): Observable<InsurancePolicyClass[]> {
    return this.crmCache
      .swr(
        `pol_id:${idInsurancePolicy}`,
        () =>
          this.http
            .get<
              InsurancePolicyClass[]
            >(environment.URL + `/insurancepolicy/ClientAreaGetInsurancePolicyId/${idInsurancePolicy}`)
            .pipe(
              catchError(error => {
                const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
                return throwError(() => errorMessage);
              }),
            ),
        { persist: false, silentPrefetch: true, ...options },
      )
      .pipe(
        map((response: any[]) => {
          return (response || []).map(item => {
            const policy = Object.assign(new InsurancePolicyClass(), item);
            policy.parseDates();
            return policy;
          });
        }),
      );
  }

  ClientAreaGetInsurancePolicyIdbyMatricula(idCustomer: number, Matricula: string): Observable<InsurancePolicyClass> {
    return this.http
      .get<InsurancePolicyClass>(
        environment.URL + `/insurancepolicy/ClientAreaGetInsurancePolicyIdbyMatricula/${idCustomer}/${Matricula}`,
      )
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetInsurancePolicyCustomerAll(
    idCustomer: number,
    options?: CrmSwrOptions,
  ): Observable<InsurancePolicyClass[]> {
    return this.crmCache
      .swr(
        `pol_cust_all:${idCustomer}`,
        () =>
          this.http
            .get<
              InsurancePolicyClass[]
            >(environment.URL + `/insurancepolicy/ClientAreaGetInsurancePolicyCustomerAll/${idCustomer}`)
            .pipe(
              catchError(error => {
                const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
                return throwError(() => errorMessage);
              }),
            ),
        { persist: false, silentPrefetch: true, ...options },
      )
      .pipe(
        map((response: any[]) => {
          return (response || []).map(item => {
            const policy = Object.assign(new InsurancePolicyClass(), item);
            policy.parseDates();
            return policy;
          });
        }),
      );
  }

  GetInsurancePolicyCustomerCompanieAll(idCustomer: number, idCompanie: number): Observable<InsurancePolicyClass[]> {
    return this.http
      .get<
        InsurancePolicyClass[]
      >(environment.URL + `/insurancepolicy/GetInsurancePolicyCustomerCompanieAll/${idCustomer}/${idCompanie}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetInsurancePolicyStatusTypesAll(options?: CrmSwrOptions): Observable<InsurancePolicyStatusTypesClass[]> {
    return this.crmCache.swr(
      'pol_status_types_all',
      () =>
        this.http
          .get<InsurancePolicyStatusTypesClass[]>(environment.URL + `/insurancepolicy/GetInsurancePolicyStatusTypesAll`)
          .pipe(
            catchError(error => {
              const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
              return throwError(() => errorMessage);
            }),
          ),
      { freshTtlMs: 5 * 60 * 1000, silentPrefetch: true, ...options },
    );
  }

  ClientAreaGetInsurancePolicyFiles(idInsurancePolicy: number, options?: CrmSwrOptions): Observable<FilesClass[]> {
    return this.crmCache.swr(
      `pol_files:${idInsurancePolicy}`,
      () =>
        this.http
          .get<
            FilesClass[]
          >(environment.URL + `/insurancepolicy/ClientAreaGetInsurancePolicyFiles/${idInsurancePolicy}`)
          .pipe(
            catchError(error => {
              const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
              return throwError(() => errorMessage);
            }),
          ),
      { persist: false, silentPrefetch: true, ...options },
    );
  }

  // ── Matrículas ASF — Enriquecimento de dados ────────────────────────

  GetVehicleRegistrationsStats(): Observable<{
    total: number;
    processadas: number;
    pendentes: number;
    erros: number;
    comViatura: number;
    comSeguro: number;
    marcas: string[];
    combustiveis: string[];
  }> {
    return this.http
      .get<{
        total: number;
        processadas: number;
        pendentes: number;
        erros: number;
        comViatura: number;
        comSeguro: number;
        marcas: string[];
        combustiveis: string[];
      }>(environment.URL + '/insurancepolicy/GetVehicleRegistrationsStats')
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetVehicleRegistrationsStatus(params?: {
    limit?: number;
    mode?: string;
    search?: string;
    estado?: string;
    combustivel?: string;
    marca?: string;
    apolices?: string;
    viatura?: string;
    seguro?: string;
  }): Observable<any[]> {
    let url = environment.URL + '/insurancepolicy/GetVehicleRegistrationsStatus';
    if (params) {
      const q = new URLSearchParams();
      if (params.limit) q.set('limit', String(params.limit));
      if (params.mode) q.set('mode', params.mode);
      if (params.search) q.set('search', params.search);
      if (params.estado) q.set('estado', params.estado);
      if (params.combustivel) q.set('combustivel', params.combustivel);
      if (params.marca) q.set('marca', params.marca);
      if (params.apolices) q.set('apolices', params.apolices);
      if (params.viatura) q.set('viatura', params.viatura);
      if (params.seguro) q.set('seguro', params.seguro);
      const queryStr = q.toString();
      if (queryStr) url += `?${queryStr}`;
    }
    return this.http.get<any[]>(url).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  ProcessVehicleRegistration(data: {
    Matricula: string;
    idInsurancePolicy: number;
    idCustomer: number;
  }): Observable<any> {
    return this.http.post<any>(environment.URL + '/insurancepolicy/ProcessVehicleRegistration', data).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  ProcessVehicleRegistrationCheckViatura(data: {
    Matricula: string;
    idInsurancePolicy?: number;
    idCustomer?: number;
  }): Observable<any> {
    return this.http.post<any>(environment.URL + '/insurancepolicy/ProcessVehicleRegistrationCheckViatura', data).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  ProcessVehicleRegistrationsBatch(): Observable<any> {
    return this.http.post<any>(environment.URL + '/insurancepolicy/ProcessVehicleRegistrationsBatch', {}).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  ImportVehicleRegistrations(payload: {
    records: any[];
    updateExisting?: boolean;
    fillOnlyEmpty?: boolean;
  }): Observable<any> {
    return this.http.post<any>(environment.URL + '/insurancepolicy/ImportVehicleRegistrations', payload).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  // ── Controlo do Robô (CRON) de Matrículas ─────────────────────────

  StartMatriculasCron(intervalMinutes: number): Observable<any> {
    return this.http.post<any>(environment.URL + '/insurancepolicy/cron/matriculas/start', { intervalMinutes }).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  StopMatriculasCron(): Observable<any> {
    return this.http.post<any>(environment.URL + '/insurancepolicy/cron/matriculas/stop', {}).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetMatriculasCronStatus(): Observable<any> {
    return this.http.get<any>(environment.URL + '/insurancepolicy/cron/matriculas/status').pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetMatriculasCronConfig(): Observable<any> {
    return this.http.get<any>(environment.URL + '/insurancepolicy/cron/matriculas/config').pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  SaveMatriculasCronConfig(config: any): Observable<any> {
    return this.http.post<any>(environment.URL + '/insurancepolicy/cron/matriculas/config', config).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  CancelVehicleRegistrationsBatch(): Observable<any> {
    return this.http.post<any>(environment.URL + '/insurancepolicy/batch/matriculas/cancel', {}).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetResolvedInteractionFilesForPolicy(
    idCustomer: number,
    idProductsTypes: number,
    idInsurancePolicy: number,
    idMessageCenterTransaction?: number,
  ): Observable<any[]> {
    let url =
      environment.URL +
      `/insurancepolicy/GetResolvedInteractionFilesForPolicy/${idCustomer}/${idProductsTypes}/${idInsurancePolicy}`;
    if (idMessageCenterTransaction && idMessageCenterTransaction > 0) {
      url += `?idMessageCenterTransaction=${idMessageCenterTransaction}`;
    }
    return this.http.get<any[]>(url).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  AssociateFilesToInsurancePolicy(payload: {
    idInsurancePolicy: number;
    idCustomer?: number;
    idMessageCenterTransaction?: number;
    files: any[];
  }): Observable<any> {
    return this.http.post<any>(environment.URL + `/insurancepolicy/AssociateFilesToInsurancePolicy`, payload).pipe(
      tap(() => {
        if (payload?.idInsurancePolicy) {
          this.crmCache.invalidate(`pol_files:${payload.idInsurancePolicy}`);
        }
        this.crmCache.invalidateCurrentScope();
      }),
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  EnrichVehicleRegistrationWithAI(payload: {
    matricula: string;
    saveToDb?: boolean;
    aiModel?: string;
  }): Observable<any> {
    return this.http.post<any>(environment.URL + '/insurancepolicy/EnrichVehicleRegistrationWithAI', payload).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetAvailableAiModels(): Observable<any> {
    return this.http.get<any>(environment.URL + '/insurancepolicy/GetAvailableAiModels').pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  DeleteVehicleRegistrations(payload: { matricula?: string; matriculas?: string[] }): Observable<any> {
    return this.http.post<any>(environment.URL + '/insurancepolicy/DeleteVehicleRegistrations', payload).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetInsuranceCompanyRequestsByPolicy(idInsurancePolicy: number): Observable<any[]> {
    return this.http
      .get<any[]>(environment.URL + `/insurancepolicy/GetInsuranceCompanyRequestsByPolicy/${idInsurancePolicy}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetInsuranceCompanyRequestDetail(idCompanyRequest: number): Observable<{
    request: any;
    notes: any[];
  }> {
    return this.http
      .get<{
        request: any;
        notes: any[];
      }>(environment.URL + `/insurancepolicy/GetInsuranceCompanyRequestDetail/${idCompanyRequest}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }
}
